import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check, ChevronLeft, ChevronRight, Copy, EyeOff, Flame, RefreshCw, Share, Sparkles, Users, X,
} from 'lucide-react';
import { PrayingHandsIcon } from '../../../components/Icons';
import { shortName } from '../../../utils/displayName';
import { fastingApi, formatPoint } from '../fastingApi';
import './FastingPrayerCard.css';

/**
 * Fasting on the prayer calendar — the list row and the detail panel.
 *
 * The cards come from GET /api/fasting/calendar: one per fast per day, listing
 * who is fasting (lib/fastingCalendar.js). `event.mine` is my own fasting day,
 * which reads "You're fasting today" instead of "Pray for …" but keeps the same
 * Generate prayer and I prayed buttons — the prayer is written for me then.
 *
 * Built on the prayer calendar's own panel classes (cal-detail-*, cal-ai-*,
 * cal-pray-btn in PrayerScreen.css) so the three kinds of card stay one design.
 * Times are always the PROGRAM's zone, like the sign-up sheet.
 */

export const FAST_COLOR = '#E8751A';
export const FAST_BG    = 'rgba(232,117,26,0.08)';

/** "Amy R., Ben C. and 2 others" — the people I would be praying for. */
export function fastNames(event, t, { max = 3 } = {}) {
  const names = [...new Set(event.people.filter((p) => !p.isMe).map((p) => shortName(p.name)).filter(Boolean))];
  const extra = Math.max(0, names.length - max) + (event.hiddenCount || 0);
  const shown = names.slice(0, max);
  if (!shown.length) return extra ? t('fasting.calendar.others', extra) : '';
  return extra ? t('fasting.calendar.namesAndOthers', shown.join(', '), extra) : shown.join(', ');
}

function dayLine(event, t) {
  return `${event.program.title} · ${t('fasting.calendar.dayOf', event.program.dayNumber, event.program.days)}`;
}

/** The row in the day list. */
export function FastingEventRow({ event, selected, onSelect, t }) {
  const names = fastNames(event, t);
  const title = event.mine
    ? (names ? t('fasting.calendar.youAndOthers', names) : t('fasting.calendar.youreFasting'))
    : t('fasting.calendar.prayFor', names || t('fasting.calendar.othersFallback'));
  return (
    <button
      type="button"
      className={`cal-event-btn cal-event-fast ${selected ? 'selected' : ''} ${event.prayedAt ? 'prayed' : ''}`}
      style={{ '--accent': FAST_COLOR, '--accent-bg': FAST_BG }}
      onClick={() => onSelect(selected ? null : { ...event, _isFast: true })}
    >
      <div className="cal-event-dot-line" style={{ background: FAST_COLOR }} />
      <div className="cal-event-info">
        <span className="cal-event-name">{title}</span>
        <span className="cal-event-rel" style={{ color: FAST_COLOR }}>{dayLine(event, t)}</span>
      </div>
      <div className="cal-event-right">
        <span className="cal-event-avatar">
          <span className="cal-event-avatar-inner fpc-avatar" style={{ '--ring': FAST_COLOR }}>
            <Flame size={18} aria-hidden="true" />
          </span>
          {event.prayedAt && <span className="cal-event-avatar-check"><Check size={10} strokeWidth={3.5} /></span>}
        </span>
      </div>
    </button>
  );
}

function prayerList(ev) {
  if (Array.isArray(ev.samplePrayerHistory) && ev.samplePrayerHistory.length) return ev.samplePrayerHistory;
  return ev.samplePrayer ? [ev.samplePrayer] : [];
}

/** The detail panel under the day list. */
export function FastingPrayerDetail({ event, onClose, onUpdate, onHidden, t, lang, MONTHS }) {
  const navigate = useNavigate();
  const [local, setLocal]       = useState(event);
  const [praying, setPraying]   = useState(false);
  const [genBusy, setGenBusy]   = useState(false);
  const [copied, setCopied]     = useState(false);
  const [aiIndex, setAiIndex]   = useState(Math.max(0, prayerList(event).length - 1));

  useEffect(() => {
    setLocal(event);
    setAiIndex(Math.max(0, prayerList(event).length - 1));
  }, [event.id]); // re-seed only when a different card is opened

  const history = prayerList(local);
  const safeIndex = Math.min(aiIndex, Math.max(0, history.length - 1));
  const current = history[safeIndex] || '';
  const tz = local.program.timezone;
  const date = new Date(`${local.dayKey}T00:00:00`);
  const names = fastNames(local, t, { max: 6 });

  function merge(next) {
    const updated = { ...local, ...(next || {}), _isFast: true };
    setLocal(updated);
    onUpdate?.(updated);
    return updated;
  }

  async function handlePray() {
    if (local.prayedAt || praying) return;
    setPraying(true);
    try {
      const d = await fastingApi.calendarPrayed(local.programId, local.dayKey);
      merge(d.event);
      setTimeout(() => { onClose(); window.scrollTo({ top: 0, behavior: 'smooth' }); }, 1200);
    } catch (err) { alert(err.message || t('prayer.couldNotMarkPrayed')); }
    finally { setPraying(false); }
  }

  async function handleGenerate() {
    setGenBusy(true);
    try {
      const d = await fastingApi.calendarGenerate(local.programId, local.dayKey);
      const updated = merge(d.event);
      setAiIndex(Math.max(0, prayerList(updated).length - 1));
    } catch (err) { alert(err.message || t('prayer.couldNotGenerate')); }
    finally { setGenBusy(false); }
  }

  async function handleCopy() {
    try { await navigator.clipboard.writeText(current); }
    catch {
      const ta = document.createElement('textarea');
      ta.value = current; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2200);
  }

  async function handleHide() {
    if (!window.confirm(t('fasting.calendar.hideConfirm', local.program.title))) return;
    try {
      await fastingApi.calendarSetHidden(local.programId, true);
      onHidden?.(local.programId);
    } catch (err) { alert(err.message || t('fasting.errors.generic')); }
  }

  const headline = local.mine
    ? t('fasting.calendar.youreFasting')
    : t('fasting.calendar.prayFor', names || t('fasting.calendar.othersFallback'));

  return (
    <div className="cal-detail-panel card fade-up fpc-panel">
      <div className="cal-detail-cover fpc-cover">
        <div className="cal-cover-overlay" />
        <button className="cal-detail-close" onClick={onClose} aria-label={t('common.close')}><X size={14} /></button>
        <div className="cal-date-badge">
          <span className="cal-date-m">{MONTHS?.[date.getMonth()]?.slice(0, 3).toUpperCase()}</span>
          <span className="cal-date-d">{date.getDate()}</span>
        </div>
        <div className="cal-cover-text">
          <div className="cal-cover-kicker"><Flame size={12} aria-hidden="true" /> {local.program.title}</div>
          <div className="cal-cover-name-inline">{headline}</div>
          <div className="cal-cover-rel">{local.program.communityName} · {t('fasting.calendar.dayOf', local.program.dayNumber, local.program.days)}</div>
          {local.prayedAt && (
            <div className="cal-cover-meta">
              <span className="cal-meta-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Check size={11} strokeWidth={3} /> {t('prayer.prayedToday')}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="cal-detail-body">
        <div className="cal-notes-section">
          <div className="cal-notes-header">
            <span className="cal-notes-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Users size={13} /> {t('fasting.calendar.whoIsFasting')}
            </span>
          </div>
          <ul className="fpc-people">
            {local.people.map((p, i) => {
              const start = formatPoint(p.startsAt, tz, lang);
              const end = formatPoint(p.endsAt, tz, lang);
              return (
                <li key={`${p.name}-${p.startsAt}-${i}`} className={p.isMe ? 'is-me' : ''}>
                  <span className="fpc-person">{p.isMe ? t('fasting.calendar.you') : shortName(p.name)}</span>
                  <span className="fpc-when">{start.time} {start.day} → {end.time} {end.day}</span>
                </li>
              );
            })}
            {local.hiddenCount > 0 && (
              <li><span className="fpc-person">{t('fasting.calendar.others', local.hiddenCount)}</span></li>
            )}
          </ul>
        </div>

        {local.purpose && (
          <div className="cal-notes-section">
            <div className="cal-notes-header">
              <span className="cal-notes-label">{t('fasting.hero.purpose')}</span>
            </div>
            <p className="fpc-purpose">{local.purpose}</p>
          </div>
        )}

        <button type="button" className="fpc-sheet-link"
          onClick={() => navigate(`/communities/${local.program.communityId}/fasting/${local.programId}`)}>
          {t('fasting.calendar.openSheet')} <ChevronRight size={14} />
        </button>

        <div className="cal-ai-section">
          {current ? (
            <div className="cal-ai-prayer-block">
              <div className="cal-ai-header">
                <span className="cal-ai-label"><Sparkles size={12} /> {t('prayer.samplePrayer')}</span>
                {history.length > 1 && (
                  <div className="cal-ai-nav">
                    <button className="cal-ai-nav-btn" onClick={() => setAiIndex((i) => Math.max(0, i - 1))}
                      disabled={safeIndex === 0} aria-label={t('prayer.previousPrayer')}><ChevronLeft size={15} /></button>
                    <span className="cal-ai-nav-count">{safeIndex + 1} / {history.length}</span>
                    <button className="cal-ai-nav-btn" onClick={() => setAiIndex((i) => Math.min(history.length - 1, i + 1))}
                      disabled={safeIndex >= history.length - 1} aria-label={t('prayer.nextPrayer')}><ChevronRight size={15} /></button>
                  </div>
                )}
              </div>
              <p className="cal-ai-text">{current}</p>
              <div className="cal-ai-actions-bar">
                <button className="cal-ai-action-btn" onClick={handleCopy}>
                  {copied ? <><Check size={14} strokeWidth={3} /> {t('prayNow.copied')}</> : <><Copy size={14} /> {t('prayer.copyPrayer')}</>}
                </button>
                <button className="cal-ai-action-btn" onClick={async () => {
                  if (navigator.share) {
                    try { await navigator.share({ title: local.program.title, text: current }); } catch { /* dismissed */ }
                  } else handleCopy();
                }}>
                  <Share size={14} /> {t('prayer.sharePrayer')}
                </button>
                <button className="cal-ai-action-btn cal-ai-action-regen" onClick={handleGenerate} disabled={genBusy}>
                  <RefreshCw size={14} /> {genBusy ? '…' : t('prayer.newPrayer')}
                </button>
              </div>
            </div>
          ) : (
            <button className="cal-ai-gen-btn" onClick={handleGenerate} disabled={genBusy}>
              <Sparkles size={13} /> {genBusy ? t('prayer.generatingPrayer') : t('prayer.generatePrayer')}
            </button>
          )}
        </div>

        <button
          className={`cal-pray-btn ${local.prayedAt ? 'prayed' : ''} ${praying ? 'praying' : ''}`}
          onClick={handlePray} disabled={!!local.prayedAt || praying}
          style={local.prayedAt ? { background: FAST_BG, color: FAST_COLOR, cursor: 'default' } : { background: FAST_COLOR }}>
          {local.prayedAt
            ? <><Check size={16} strokeWidth={3} color="currentColor" /> {t('prayer.prayedButton')}</>
            : praying
              ? <><PrayingHandsIcon size={16} color="currentColor" /> {t('prayer.prayingButton')}</>
              : <><PrayingHandsIcon size={16} color="currentColor" /> {t('prayer.prayButton')}</>}
        </button>

        <button type="button" className="fpc-hide" onClick={handleHide}>
          <EyeOff size={13} aria-hidden="true" /> {t('fasting.calendar.hide')}
        </button>
      </div>
    </div>
  );
}
